
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useCourseStore from '../stores/useCourseStore';
import useAuthStore from '../stores/useAuthStore';
import DashboardLayout from '../components/DashboardLayout';
import MediaSelectorModal from '../components/MediaSelectorModalAdmin';
import FloatingChatButton from '../components/FloatingChatButton';

import CryptoJS from 'crypto-js'

import { Editor } from '@tinymce/tinymce-react';

// Import DND Kit
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useToast } from '../components/ToastContainer';
import CourseCreationGuideModal from '../components/CourseCreationGuideModal';
import axiosClient from '../services/api';
import useCourseInstructor from '../components/DeciferInstructorId';
// Helper to generate temporary IDs
const generateTempId = (prefix = 'temp') => `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
// Drop Indicator Component
const DropIndicator = ({ isVisible }) => {
    if (!isVisible) return null;
    return (
        <div className="my-2 mx-4 h-1 bg-primary-500 rounded-full animate-pulse" />
    );
};
// Reusable Sortable Item Component
const SortableItem = ({ id, children, disabled = false }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id, disabled });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 100 : 1,
        opacity: isDragging ? 0.5 : 1,
    };
    // If children is a function, call it with drag props
    if (typeof children === 'function') {
        return children({ attributes, listeners, setNodeRef, transform, transition, isDragging });
    }
    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing"
        >
            {children}
        </div>
    );
};
const AdminCourseCreatePage = () => {
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const location = useLocation()
    const { id } = useParams();
    const toast = useToast();
    const { createCourse, updateCourse, fetchISACourse, generateDescription, fetchCategories, categories: allCategories, isLoading, error, clearError } = useCourseStore();
    const [isEditing, setIsEditing] = useState(false);
    const SECRET_KEY = process.env.REACT_APP_PUBLIC_CRYPT_KEY;
    const [courseType, setCourseType] = useState('full online');
    const [onsiteProof, setOnsiteProof] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewOnsite, setPreviewOnsite] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
    const [currentLessonContext, setCurrentLessonContext] = useState({
        moduleIndex: null,
        lessonIndex: null,
        contentType: null
    });
    const [activeModuleId, setActiveModuleId] = useState(null);
    const [overModuleId, setOverModuleId] = useState(null);
    const [activeOutcomeId, setActiveOutcomeId] = useState(null);
    const [overOutcomeId, setOverOutcomeId] = useState(null);
    const [isUsingAIDescription, setIsUsingAIDescription] = useState(false);
    const CheckInstructorSelected = useCourseInstructor();
    const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
    const [isCourseLoaded, setIsCourseLoaded] = useState(false);

    const [isTextModalOpen, setIsTextModalOpen] = useState(false);
    const [activeLesson, setActiveLesson] = useState(null);
    const [editorContent, setEditorContent] = useState('');

    const openTextEditorModal = (moduleIndex, lessonIndex) => {
        const lesson = formData.modules[moduleIndex].lessons[lessonIndex];

        setActiveLesson({ moduleIndex, lessonIndex });
        setEditorContent(lesson.textDescription || '');
        setIsTextModalOpen(true);
    };

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '',
        price: 45000,
        duration: '8 weeks',
        level: 'Beginner',
        instructor: '',
        thumbnail: null,
        introVideo: null,
        requirements: ['Basic computer skills', 'Internet connection'],
        whatYouWillLearn: [
            { id: generateTempId('outcome'), text: 'Industry standard expert-led experience' },
        ],
        newRequirement: '',
        newLearningOutcome: '',
        modules: [
            {
                id: generateTempId('module'),
                title: 'Getting Started',
                lessons: [
                    {
                        id: generateTempId('lesson'),
                        title: 'Introduction to lesson',
                        video: null,
                        resources: [],
                        quiz: null,
                        assignment: null,
                        textDescription: ''
                    }
                ]
            }
        ]
    });

    const [oilAndGasCategories, setOilAndGasCategories] = useState([]);

    const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);

    const [instructors, setInstructors] = useState([]);

    useEffect(() => {
        if (user.role === 'admin') {
            const fetchInstructors = async () => {
                try {
                    const res = await axiosClient.get('/users/admin/users', {
                        params: {
                            role: 'instructor',
                            adminVerified: true
                        }
                    });
                    setInstructors(res.data.users);
                } catch (err) {
                    toast.error('Failed to load instructors');
                }
            };
            fetchInstructors();
        }
    }, [user.role, toast]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            clearError();
        }
    }, [error, toast]);

    // Recursive flatten function (same as CourseCatalog)
    const flattenCategories = useCallback((category, depth = 0) => {
        let result = [{ ...category, depth }];
        if (category.children && category.children.length > 0) {
            category.children.forEach(child => {
                result = result.concat(flattenCategories(child, depth + 1));
            });
        }
        return result;
    }, []);

    useEffect(() => {
        const fetchAndFilterCategories = async () => {
            await fetchCategories();
        };
        fetchAndFilterCategories();
    }, [fetchCategories]);

    useEffect(() => {
        if (allCategories.length > 0) {
            const root = allCategories.find(
                cat =>
                    cat.name.toLowerCase() === 'oil and gas courses' ||
                    cat.slug === 'oil-and-gas-courses'
            );
            if (root) {
                const flat = flattenCategories(root);
                setOilAndGasCategories(flat);
            }
        }
    }, [allCategories, flattenCategories]);

    useEffect(() => {
        if (error) {
            toast.info(error);
            clearError();
        }
    }, [error, toast]);

    useEffect(() => {
        const loadDraft = () => {
            const draftKey = `courseDraft-${id || "new"}`;
            const draft = localStorage.getItem(draftKey);
            if (!draft) return;

            try {
                const parsed = JSON.parse(draft);

                if (id && parsed.courseId && parsed.courseId !== id) return;

                setCourseType(parsed.courseType);

                if (parsed.formData?.thumbnail) {
                    setPreview(`${parsed.formData.thumbnail}?t=${Date.now()}`);
                }

                if (parsed.onsiteProof) {
                    setPreviewOnsite(`${parsed.onsiteProof}?t=${Date.now()}`);
                }

                const cleanId = (val) => {
                    if (!val || val === "null" || val === "undefined") return null;
                    return val;
                };

                parsed.formData.modules = parsed.formData.modules.map(module => ({
                    ...module,
                    lessons: module.lessons.map(lesson => ({
                        ...lesson,
                        video: cleanId(lesson.video),
                        quiz: cleanId(lesson.quiz),
                        assignment: cleanId(lesson.assignment),
                        resources: lesson.resources?.map(cleanId).filter(Boolean) || []
                    }))
                }));

                setFormData(parsed.formData);

                toast.info(
                    `📄 Draft restored from ${new Date(parsed.lastSaved).toLocaleString()}. 
        Please re-upload media files if needed.`
                );
            } catch (e) {
                console.error("Failed to parse draft:", e);
                localStorage.removeItem(draftKey);
            }
        };

        if (id) {
            setIsEditing(true);

            loadCourseData(id).then(() => {
                setIsCourseLoaded(true);
            });

            localStorage.removeItem(`courseDraft-${id}`);
            localStorage.removeItem('courseDraft-new');

        } else {
            loadDraft();
            setIsCourseLoaded(true);
        }

    }, [id, location]);


    useEffect(() => {
        const checkForPendingAttachments = () => {
            if (!formData.modules?.length) return;
            if (!isCourseLoaded) return;

            let hasChanges = false;

            const updatedModules = formData.modules.map(module => {
                const updatedLessons = module.lessons.map(lesson => {
                    // Use the guaranteed lesson.id (either _id or temp-id)
                    const quizKey = `lesson-${lesson.id}-quiz`;
                    const assignmentKey = `lesson-${lesson.id}-assignment`;

                    const pendingQuizId = localStorage.getItem(quizKey);
                    const pendingAssignmentId = localStorage.getItem(assignmentKey);

                    let updatedLesson = { ...lesson };

                    if (pendingQuizId && !lesson.quiz) {
                        console.log(`✅ Found pending quiz for ${lesson.id}: ${pendingQuizId}`);
                        updatedLesson.quiz = pendingQuizId;
                        hasChanges = true;
                        localStorage.removeItem(quizKey);
                    }

                    if (pendingAssignmentId && !lesson.assignment) {
                        console.log(`✅ Found pending assignment for ${lesson.id}: ${pendingAssignmentId}`);
                        updatedLesson.assignment = pendingAssignmentId;
                        hasChanges = true;
                        localStorage.removeItem(assignmentKey);
                    }

                    return updatedLesson;
                });

                return { ...module, lessons: updatedLessons };
            });

            if (hasChanges) {
                setFormData(prev => ({
                    ...prev,
                    modules: updatedModules
                }));
            }
        };

        const timer = setTimeout(() => {
            checkForPendingAttachments();
        }, 300);
        return () => clearTimeout(timer);

    }, [location, isCourseLoaded, formData.modules]);

    useEffect(() => {
        if (isSubmitting || !isCourseLoaded) return;

        const saveDraft = async () => {
            try {
                const getBase64 = (file) =>
                    new Promise((resolve, reject) => {
                        if (!(file instanceof File)) return resolve(file);
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(file);
                    });

                const [thumbnailBase64, onsiteProofBase64] = await Promise.all([
                    getBase64(formData.thumbnail),
                    getBase64(onsiteProof),
                ]);

                const serializableFormData = {
                    ...formData,
                    thumbnail: thumbnailBase64 || formData.thumbnail,
                    modules: formData.modules.map((module) => ({
                        ...module,
                        lessons: module.lessons.map((lesson) => ({ ...lesson })),
                    })),
                };

                const draft = {
                    formData: serializableFormData,
                    courseType,
                    onsiteProof: onsiteProofBase64 || onsiteProof,
                    lastSaved: new Date().toISOString(),
                    isEditing,
                    courseId: id || "new",
                };

                const draftKey = `courseDraft-${id || "new"}`;
                localStorage.setItem(draftKey, JSON.stringify(draft));
            } catch (err) {
                console.error("Failed to save draft:", err);
            }
        };

        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [formData, courseType, onsiteProof, isSubmitting, id, isEditing, isCourseLoaded]);


    const handleMediaSelect = (selectedMedia) => {
        if (!selectedMedia || selectedMedia.length === 0) return;

        const mediaItem = selectedMedia[0];
        if (!mediaItem?._id) return;

        const { moduleIndex, lessonIndex, contentType } = currentLessonContext;

        if (contentType === 'introVideo') {
            setFormData(prev => ({
                ...prev,
                introVideo: mediaItem._id,
            }));
        }

        else if (
            (mediaItem.type === 'videos' || mediaItem.type === 'video') &&
            moduleIndex !== null &&
            lessonIndex !== null
        ) {
            updateLesson(moduleIndex, lessonIndex, 'video', mediaItem._id);
        }

        else if (
            (mediaItem.type === 'resources' || mediaItem.type === 'resource') &&
            moduleIndex !== null &&
            lessonIndex !== null
        ) {
            const currentResources = formData.modules?.[moduleIndex]?.lessons?.[lessonIndex]?.resources || [];

            if (!currentResources.includes(mediaItem._id)) {
                updateLesson(moduleIndex, lessonIndex, 'resources', [
                    ...currentResources,
                    mediaItem._id,
                ]);
            }
        }

        setIsMediaModalOpen(false);
    };


    const handleModuleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = formData.modules.findIndex(module => module.id === active.id);
        const newIndex = formData.modules.findIndex(module => module.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        setFormData(prev => ({
            ...prev,
            modules: arrayMove(prev.modules, oldIndex, newIndex)
        }));
    };

    // Handle Learning Outcome Reordering
    const handleLearningOutcomeDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = formData.whatYouWillLearn.findIndex(outcome => outcome.id === active.id);
        const newIndex = formData.whatYouWillLearn.findIndex(outcome => outcome.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        setFormData(prev => ({
            ...prev,
            whatYouWillLearn: arrayMove(prev.whatYouWillLearn, oldIndex, newIndex)
        }));
    };

    const loadCourseData = async (courseId) => {
        try {
            const courseData = await fetchISACourse(courseId);
            const modulesWithIds = (courseData.modules || []).map((module) => ({
                ...module,
                id: module._id || generateTempId('module'),
                lessons: (module.lessons || []).map((lesson) => ({
                    ...lesson,
                    id: lesson._id || generateTempId('lesson'),
                    video: lesson.video || null,
                    resources: lesson.resources || [],
                    quiz: lesson.quiz || null,
                    assignment: lesson.assignment || null,
                    textDescription: lesson.textDescription || ''
                })),
            }));
            const outcomesWithIds = (courseData.whatYouWillLearn || []).map(text => ({
                id: generateTempId('outcome'),
                text: text
            }));
            if (courseData) {
                setFormData({
                    title: courseData.title || '',
                    description: courseData.description || '',
                    category: courseData.category || '',
                    price: courseData.price || 49,
                    duration: courseData.duration || '8 weeks',
                    level: courseData.level || 'Beginner',
                    thumbnail: courseData.thumbnail || null,
                    introVideo: courseData.introVideo || null,
                    requirements: courseData.requirements || [],
                    whatYouWillLearn: outcomesWithIds,
                    newRequirement: '',
                    newLearningOutcome: '',
                    modules: modulesWithIds.length > 0 ? modulesWithIds : [
                        {
                            id: generateTempId('module'),
                            title: 'Getting Started',
                            lessons: [
                                {
                                    id: generateTempId('lesson'),
                                    title: 'Introduction to lesson',
                                    video: null,
                                    resources: [],
                                    quiz: null,
                                    assignment: null,
                                    textDescription: ''
                                }
                            ]
                        }
                    ]
                });
                setCourseType(courseData.courseType || 'full online');
                setOnsiteProof(courseData.onsiteProof || null);

                if (courseData.onsiteProof) {
                    const fullUrl = courseData.onsiteProof.startsWith('http')
                        ? courseData.onsiteProof
                        : `${process.env.REACT_APP_SOCKET_URL}${courseData.onsiteProof}`;

                    setPreviewOnsite(`${fullUrl}?t=${Date.now()}`);
                }

                if (courseData.thumbnail) {
                    const fullUrl = courseData.thumbnail.startsWith('http')
                        ? courseData.thumbnail
                        : `${process.env.REACT_APP_SOCKET_URL}${courseData.thumbnail}`;

                    setPreview(`${fullUrl}?t=${Date.now()}`);
                }
            }
        } catch (error) {
            console.error('Error loading course ', error);
            toast.success('Failed to load course data')
        } finally {
            setIsCourseLoaded(true);
        }
    };

    const addRequirement = () => {
        if (formData.newRequirement.trim()) {
            setFormData(prev => ({
                ...prev,
                requirements: [...prev.requirements, prev.newRequirement.trim()],
                newRequirement: ''
            }));
        }
    };

    const removeRequirement = (index) => {
        setFormData(prev => ({
            ...prev,
            requirements: prev.requirements.filter((_, i) => i !== index)
        }));
    };

    const addLearningOutcome = () => {
        if (formData.newLearningOutcome.trim()) {
            const newOutcome = {
                id: generateTempId('outcome'),
                text: formData.newLearningOutcome.trim()
            };
            setFormData(prev => ({
                ...prev,
                whatYouWillLearn: [...prev.whatYouWillLearn, newOutcome],
                newLearningOutcome: ''
            }));
        }
    };

    const removeLearningOutcome = (index) => {
        setFormData(prev => ({
            ...prev,
            whatYouWillLearn: prev.whatYouWillLearn.filter((_, i) => i !== index)
        }));
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, thumbnail: file }));
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleOnsiteProofChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setOnsiteProof(file);
            setPreviewOnsite(URL.createObjectURL(file));
        }
    };

    const addModule = () => {
        const newId = generateTempId('module');
        setFormData(prev => ({
            ...prev,
            modules: [...prev.modules, {
                id: newId,
                title: 'New Module',
                lessons: []
            }]
        }));
    };

    const updateModule = (moduleIndex, field, value) => {
        setFormData(prev => ({
            ...prev,
            modules: prev.modules.map((module, i) =>
                i === moduleIndex ? { ...module, [field]: value } : module
            )
        }));
    };

    const addLesson = (moduleIndex) => {
        const newLessonId = generateTempId('lesson');
        setFormData(prev => ({
            ...prev,
            modules: prev.modules.map((module, i) =>
                i === moduleIndex
                    ? {
                        ...module,
                        lessons: [
                            ...module.lessons,
                            {
                                id: newLessonId,
                                title: 'New Lesson',
                                video: null,
                                resources: [],
                                quiz: null,
                                assignment: null,
                                textDescription: ''
                            }
                        ]
                    }
                    : module
            )
        }));
    };

    const updateLesson = (moduleIndex, lessonIndex, field, value) => {
        setFormData(prev => ({
            ...prev,
            modules: prev.modules.map((module, i) =>
                i === moduleIndex
                    ? {
                        ...module,
                        lessons: module.lessons.map((lesson, j) =>
                            j === lessonIndex ? { ...lesson, [field]: value } : lesson
                        )
                    }
                    : module
            )
        }));
    };

    const saveTextDescription = () => {
        const { moduleIndex, lessonIndex } = activeLesson;

        const updatedModules = [...formData.modules];
        updatedModules[moduleIndex].lessons[lessonIndex].textDescription = editorContent;

        setFormData({
            ...formData,
            modules: updatedModules
        });

        setIsTextModalOpen(false);
    };


    const prepareModulesForSubmission = (modules) => {
        return modules.map(module => {
            const { id, ...moduleRest } = module;
            return {
                ...moduleRest,
                _id: module._id || undefined,
                lessons: module.lessons.map(lesson => {
                    const { id: lessonId, ...lessonRest } = lesson;
                    const payloadLesson = { ...lessonRest };

                    if (lesson._id) {
                        payloadLesson._id = lesson._id;
                    }
                    return payloadLesson;
                })
            };
        });
    };

    const prepareOutcomesForSubmission = (outcomes) => {
        return outcomes.map(outcome => outcome.text);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        if (!formData.title.trim()) {
            toast.success('Please enter a course title');
            setIsSubmitting(false);
            return;
        }
        if (!formData.description.trim()) {
            toast.success('Please enter a course description');
            setIsSubmitting(false);
            return;
        }
        if (courseType === 'online onsite' && !onsiteProof) {
            toast.success('Please upload onsite proof for online onsite courses');
            setIsSubmitting(false);
            return;
        }
        try {

            const payload = new FormData();

            payload.append('title', formData.title);
            payload.append('description', formData.description);
            payload.append('category', formData.category?._id || formData.category);
            payload.append('price', formData.price.toString());
            payload.append('duration', formData.duration);
            payload.append('level', formData.level);
            payload.append('courseType', courseType);

            if (formData.introVideo) {
                payload.append('introVideo', formData.introVideo);
            }

            payload.append('instructor', formData.instructor);

            payload.append('requirements', JSON.stringify(formData.requirements));
            payload.append('whatYouWillLearn', JSON.stringify(prepareOutcomesForSubmission(formData.whatYouWillLearn)));

            const cleanId = (val) => {
                if (!val || val === "null" || val === "undefined") return null;
                return val;
            };

            const cleanedModules = prepareModulesForSubmission(formData.modules).map(module => ({
                ...module,
                lessons: module.lessons.map(lesson => ({
                    ...lesson,
                    video: cleanId(lesson.video),
                    quiz: cleanId(lesson.quiz),
                    assignment: cleanId(lesson.assignment),
                    resources: lesson.resources?.map(cleanId).filter(Boolean) || []
                }))
            }));

            payload.append('modules', JSON.stringify(cleanedModules));

            if (formData.thumbnail instanceof File) {
                payload.append('thumbnail', formData.thumbnail);
            } else if (typeof formData.thumbnail === 'string' && formData.thumbnail.trim() !== '') {
                payload.append('thumbnail', formData.thumbnail);
            }

            if (courseType === 'online onsite') {
                if (onsiteProof instanceof File) {
                    payload.append('onsiteProof', onsiteProof);
                } else if (typeof onsiteProof === 'string' && onsiteProof.trim() !== '') {
                    payload.append('onsiteProof', onsiteProof);
                }
            }

            let result;
            if (isEditing) {
                result = await updateCourse(id, payload);
            } else {
                result = await createCourse(payload);
            }

            if (result && !isEditing) {
                const draftKey = `courseDraft-${id || "new"}`;
                localStorage.removeItem(draftKey);
                toast.success(`${isEditing ? 'Course updated' : 'Course created'} successfully! ${user?.role === 'admin' ? '' : 'It will be reviewed by our admin team.'}`)
                navigate('/admin/cloud/dashboard');
            } else if (result && isEditing) {
                const draftKey = `courseDraft-${id || "new"}`;
                localStorage.removeItem(draftKey);
                toast.success(`${isEditing ? 'Course updated' : 'Course created'} successfully! ${user?.role === 'admin' ? '' : 'It will be reviewed by our admin team.'}`)
            }

        } catch (error) {
            console.error('Error saving course:', error);
            toast.error(`Failed to ${isEditing ? 'update' : 'create'} course`)
        } finally {
            setIsSubmitting(false);
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragStart = () => {
        const activeElement = document.activeElement;
        if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
            activeElement.blur();
        }
    };

    const draftRaw = !id ? localStorage.getItem("courseDraft-new") : null;

    let draftData = null;

    try {
        draftData = draftRaw ? JSON.parse(draftRaw) : null;
    } catch (e) {
        console.error("Invalid draft JSON");
    }

    if (isLoading) {
        return (
            <DashboardLayout userRole={user.role}>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                </div>
            </DashboardLayout>
        );
    }


    return (
        <DashboardLayout userRole={user.role}>
            <div className="max-w-4xl mx-auto p-6">
                <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-6">

                    {/* Header with Back Button */}
                    {isEditing && (
                        <div className="mb-6 flex justify-between items-center">
                            <div>
                                <button
                                    onClick={() => navigate(-1)}
                                    className="flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 mb-2"
                                >
                                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                    </svg>
                                    Go Back
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="mb-8 flex justify-between items-end">
                        <div>
                            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
                                {isEditing ? 'Edit Course' : 'Create New Course'}
                            </h1>
                            <p className="text-neutral-600 text-xl dark:text-neutral-400 mt-2">
                                Build your course structure and add content
                            </p>
                        </div>
                        <button
                            onClick={() => setIsGuideModalOpen(true)}
                            className="px-3 py-1 text-sm bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded"
                        >
                            📖 How to Create a Course
                        </button>
                    </div>


                </h1>
                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Basic Information */}
                    {!id && draftData && (
                        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-yellow-800 dark:text-yellow-200">
                                    📄 Draft saved on {new Date(JSON.parse(localStorage.getItem(`courseDraft-${id || "new"}`)).lastSaved).toLocaleString()}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (window.confirm('Are you sure you want to clear the saved draft?')) {
                                            localStorage.removeItem(`courseDraft-${id || "new"}`);
                                            window.location.reload();
                                        }
                                    }}
                                    className="text-xs text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-300 font-medium"
                                >
                                    Clear Draft
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-6">
                            Basic Information
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="title" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    Course Title
                                </label>
                                <input
                                    type="text"
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                    required
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    placeholder="Enter course title"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    Category *
                                </label>
                                <select
                                    value={formData.category?._id}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    required
                                >
                                    <option value="">Select a category</option>
                                    {oilAndGasCategories.map(cat => (
                                        <option key={cat._id} value={cat._id}>
                                            {`${"— ".repeat(cat.depth)}${cat.name}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="price" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    Price (₦)
                                </label>
                                <input
                                    type="number"
                                    id="price"
                                    value={formData.price}
                                    onChange={(e) => setFormData(prev => ({ ...prev, price: parseInt(e.target.value) }))}
                                    min="0"
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div>
                                <label htmlFor="duration" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    Duration
                                </label>
                                <input
                                    type="text"
                                    id="duration"
                                    value={formData.duration}
                                    onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    placeholder="e.g., 8 weeks"
                                />
                            </div>
                            <div>
                                <label htmlFor="level" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    Level
                                </label>
                                <select
                                    id="level"
                                    value={formData.level}
                                    onChange={(e) => setFormData(prev => ({ ...prev, level: e.target.value }))}
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    <option value="Beginner">Beginner</option>
                                    <option value="Intermediate">Intermediate</option>
                                    <option value="Advanced">Advanced</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    Course Type
                                </label>
                                <div className="flex space-x-4">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="courseType"
                                            value="full online"
                                            checked={courseType === 'full online'}
                                            onChange={(e) => setCourseType(e.target.value)}
                                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 dark:border-neutral-600"
                                        />
                                        <span className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Full Online</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="courseType"
                                            value="online onsite"
                                            checked={courseType === 'online onsite'}
                                            onChange={(e) => setCourseType(e.target.value)}
                                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 dark:border-neutral-600"
                                        />
                                        <span className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Online Onsite</span>
                                    </label>
                                </div>
                            </div>

                            {user.role === 'admin' && (
                                <div>
                                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                        Assign Instructor *
                                    </label>
                                    <select
                                        value={formData.instructor}
                                        onChange={(e) => {
                                            const instructorId = e.target.value;
                                            setFormData(prev => ({ ...prev, instructor: instructorId }));
                                            if (instructorId) {
                                                try {
                                                    const encrypted = CryptoJS.AES.encrypt(instructorId, SECRET_KEY).toString();
                                                    localStorage.setItem('courseInstructor', encrypted);
                                                } catch (err) {
                                                    console.error('Failed to encrypt instructor ID', err);
                                                }
                                            } else {
                                                localStorage.removeItem('courseInstructor');
                                            }
                                        }}
                                        className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        required
                                    >
                                        <option value="">Select an instructor</option>
                                        {instructors?.map(inst => (
                                            <option key={inst?._id} value={inst?._id}>
                                                {inst?.name} ({inst?.email})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Thumbnail Upload */}
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-6">
                            Course Thumbnail
                        </h2>
                        <div className="flex items-center space-x-6">
                            <div className="w-32 h-32 bg-neutral-100 dark:bg-neutral-700 rounded-lg flex items-center justify-center overflow-hidden">
                                {(preview || formData.thumbnail) ? (
                                    <img
                                        src={
                                            preview
                                                ? preview
                                                : formData.thumbnail instanceof File
                                                    ? URL.createObjectURL(formData.thumbnail)
                                                    : `${process.env.REACT_APP_SOCKET_URL}${formData.thumbnail}`
                                        }
                                        alt="Thumbnail"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-neutral-400 dark:text-neutral-500">No image</span>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    Upload Thumbnail
                                </label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleThumbnailChange}
                                    className="block w-full text-sm text-neutral-700 dark:text-neutral-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-primary-900/20 dark:file:text-primary-300"
                                />
                                <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-500">
                                    Recommended size: 300x200 pixels
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* Intro Video */}
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-6">
                            Intro Video (Optional)
                        </h2>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                            A short trailer to showcase your course (max 2 mins recommended)
                        </p>
                        <div className="flex items-center gap-4 mt-3">
                            {formData.introVideo ? (
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-green-600 dark:text-green-400">
                                        ✅ Video selected
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, introVideo: null }))}
                                        className="text-xs text-danger-600 hover:text-danger-800 dark:text-danger-400"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCurrentLessonContext({ contentType: 'introVideo' });
                                        setIsMediaModalOpen(true);
                                    }}
                                    className="px-3 py-1 bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium rounded"
                                >
                                    Select Intro Video
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Description */}

                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                                Course Description
                            </h2>
                            <div className="items-center space-x-3 hidden">
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={isUsingAIDescription}
                                        onChange={(e) => setIsUsingAIDescription(e.target.checked)}
                                        className="sr-only"
                                    />
                                    <div className={`relative w-10 h-5 rounded-full transition-colors duration-300 ${isUsingAIDescription ? 'bg-primary-500' : 'bg-neutral-300 dark:bg-neutral-600'}`}>
                                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-300 ${isUsingAIDescription ? 'translate-x-5' : ''}`}></div>
                                    </div>
                                    <span className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Use AI</span>
                                </label>
                                {isUsingAIDescription && (
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            if (!formData.title.trim()) {
                                                toast.info('Please enter a course title first')
                                                return;
                                            }
                                            setIsGeneratingDescription(true);
                                            const generated = await generateDescription({
                                                title: formData.title,
                                                level: formData.level
                                            }, 'course');
                                            if (generated) {
                                                setFormData(prev => ({ ...prev, description: generated }));
                                            } else {
                                                toast.error('Failed to generate description. Please try again or write manually.')
                                            }
                                            setIsGeneratingDescription(false);
                                        }}
                                        disabled={isGeneratingDescription || !formData.title.trim()}
                                        className="px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-xs font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 disabled:opacity-50"
                                    >
                                        {isGeneratingDescription ? (
                                            <span className="flex items-center">
                                                <svg className="animate-spin h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Generating...
                                            </span>
                                        ) : (
                                            'Generate with AI'
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>

                        {isUsingAIDescription ? (
                            <div>
                                <label htmlFor="description" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    AI-Generated Description (Edit as needed)
                                </label>
                                <Editor
                                    apiKey="z7v1her3jufqpkft4ixbai7zc3c88cb3706r531rzjmn2oc6"
                                    value={formData.description}
                                    onEditorChange={(content) => setFormData(prev => ({ ...prev, description: content }))}
                                    init={{
                                        height: 300,
                                        menubar: false,
                                        plugins: [
                                            'advlist autolink lists link image charmap print preview anchor',
                                            'searchreplace visualblocks code fullscreen',
                                            'insertdatetime media table paste code help wordcount'
                                        ],
                                        toolbar:
                                            'undo redo | formatselect | bold italic backcolor | ' +
                                            'alignleft aligncenter alignright alignjustify | ' +
                                            'bullist numlist outdent indent | removeformat | help',
                                        content_style: 'body { font-family: Inter, sans-serif; font-size: 14px; }',
                                        skin: 'oxide-dark',
                                        content_css: 'dark'
                                    }}
                                />
                            </div>
                        ) : (
                            <div>
                                <label htmlFor="description" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    Description
                                </label>
                                <Editor
                                    apiKey="z7v1her3jufqpkft4ixbai7zc3c88cb3706r531rzjmn2oc6"
                                    value={formData.description}
                                    onEditorChange={(content) => setFormData(prev => ({ ...prev, description: content }))}
                                    init={{
                                        height: 300,
                                        menubar: false,
                                        plugins: [
                                            "advlist", "anchor", "autolink", "charmap", "code", "fullscreen",
                                            "insertdatetime", "link", "lists",
                                            "preview", "searchreplace", "table", "visualblocks",
                                        ],

                                        toolbar: "undo redo | styles | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify",

                                        content_style: 'body { font-family: Inter, sans-serif; font-size: 14px; }',
                                        skin: 'oxide-dark',
                                        content_css: 'dark'
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Requirements */}
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-6">
                            Requirements
                        </h2>
                        <div className="space-y-4">
                            {formData.requirements.map((requirement, index) => (
                                <div key={index} className="flex items-center space-x-3">
                                    <span className="flex-1 text-neutral-900 dark:text-white">{requirement}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeRequirement(index)}
                                        className="text-danger-600 hover:text-danger-800 dark:text-danger-400 dark:hover:text-danger-300"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                            <div className="flex space-x-3">
                                <input
                                    type="text"
                                    value={formData.newRequirement}
                                    onChange={(e) => setFormData(prev => ({ ...prev, newRequirement: e.target.value }))}
                                    className="flex-1 px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    placeholder="Add a requirement"
                                />
                                <button
                                    type="button"
                                    onClick={addRequirement}
                                    className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                    {/* What You Will Learn — WITH DROP INDICATORS */}
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">What You Will Learn</h2>
                            <div className="flex space-x-2">
                                <input
                                    type="text"
                                    value={formData.newLearningOutcome}
                                    onChange={(e) => setFormData(prev => ({ ...prev, newLearningOutcome: e.target.value }))}
                                    onKeyPress={(e) => e.key === 'Enter' && addLearningOutcome()}
                                    className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    placeholder="Add a learning outcome"
                                />
                                <button
                                    type="button"
                                    onClick={addLearningOutcome}
                                    className="px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={(event) => {
                                setActiveOutcomeId(event.active.id);
                                handleDragStart();
                            }}
                            onDragOver={(event) => {
                                setOverOutcomeId(event.over?.id || null);
                            }}
                            onDragEnd={(event) => {
                                handleLearningOutcomeDragEnd(event);
                                setActiveOutcomeId(null);
                                setOverOutcomeId(null);
                            }}
                        >
                            <SortableContext
                                items={formData.whatYouWillLearn.map(outcome => outcome.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <ul className="space-y-2">
                                    {formData.whatYouWillLearn.map((outcome, index) => (
                                        <React.Fragment key={outcome.id}>
                                            {/* DROP INDICATOR BEFORE ITEM */}
                                            {activeOutcomeId && overOutcomeId === outcome.id && (
                                                <DropIndicator isVisible={true} />
                                            )}
                                            <SortableItem key={outcome.id} id={outcome.id}>
                                                {({ attributes, listeners, setNodeRef, transform, transition }) => (
                                                    <li
                                                        ref={setNodeRef}
                                                        style={{
                                                            transform: CSS.Transform.toString(transform),
                                                            transition,
                                                        }}
                                                        className="flex items-center p-2 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg"
                                                    >
                                                        {/* 👇 ISOLATED DRAG HANDLE — type="button" added */}
                                                        <button
                                                            type="button"
                                                            {...attributes}
                                                            {...listeners}
                                                            className="p-1 mr-2 text-neutral-400 hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300 cursor-grab active:cursor-grabbing"
                                                            aria-label="Drag to reorder"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                                            </svg>
                                                        </button>
                                                        <span className="font-mono text-sm text-neutral-500 dark:text-neutral-400 mr-2">
                                                            {index + 1}.
                                                        </span>
                                                        <input
                                                            type="text"
                                                            value={outcome.text}
                                                            onChange={(e) => {
                                                                const newOutcomes = [...formData.whatYouWillLearn];
                                                                newOutcomes[index] = { ...outcome, text: e.target.value };
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    whatYouWillLearn: newOutcomes
                                                                }));
                                                            }}
                                                            className="flex-1 px-2 py-1 border-none bg-transparent text-neutral-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500 rounded"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => removeLearningOutcome(index)}
                                                            className="ml-2 p-1 text-danger-500 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </li>
                                                )}
                                            </SortableItem>
                                        </React.Fragment>
                                    ))}
                                    {/* DROP INDICATOR AT END OF LIST */}
                                    {activeOutcomeId && overOutcomeId === 'learning-outcomes-end' && (
                                        <DropIndicator isVisible={true} />
                                    )}
                                </ul>
                            </SortableContext>
                        </DndContext>
                    </div>
                    {/* Onsite Proof (if applicable) */}
                    {courseType === 'online onsite' && (
                        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-6">
                                Onsite Proof
                            </h2>

                            <div className="w-32 h-32 bg-neutral-100 dark:bg-neutral-700 rounded-lg flex items-center justify-center overflow-hidden">
                                {(previewOnsite || formData.onsiteProof) ? (
                                    <img
                                        src={
                                            previewOnsite
                                                ? previewOnsite
                                                : formData.onsiteProof instanceof File
                                                    ? URL.createObjectURL(formData.onsiteProof)
                                                    : `${process.env.REACT_APP_SOCKET_URL}${formData.onsiteProof}`
                                        }
                                        alt="OnsiteProof"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-neutral-400 dark:text-neutral-500">No image</span>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                    Upload Proof of Onsite Facility
                                </label>
                                <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    onChange={handleOnsiteProofChange}
                                    className="block w-full text-sm text-neutral-700 dark:text-neutral-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-primary-900/20 dark:file:text-primary-300"
                                />
                                <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-500">
                                    Upload images or PDF documentation of your onsite facility
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Modules & Lessons — DRAG AND DROP FOR LESSONS REMOVED */}
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Course Structure</h2>
                            <button
                                type="button"
                                onClick={addModule}
                                className="px-3 py-1 bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
                            >
                                Add Module
                            </button>
                        </div>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={(event) => {
                                setActiveModuleId(event.active.id);
                                handleDragStart();
                            }}
                            onDragOver={(event) => {
                                setOverModuleId(event.over?.id || null);
                            }}
                            onDragEnd={(event) => {
                                handleModuleDragEnd(event);
                                setActiveModuleId(null);
                                setOverModuleId(null);
                            }}
                        >
                            <SortableContext
                                items={formData.modules.map(module => module.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {formData.modules.map((module, moduleIndex) => (
                                    <React.Fragment key={module.id}>
                                        {/* DROP INDICATOR BEFORE MODULE */}
                                        {activeModuleId && overModuleId === module.id && (
                                            <DropIndicator isVisible={true} />
                                        )}
                                        <SortableItem key={module.id} id={module.id}>
                                            {({ attributes, listeners, setNodeRef, transform, transition }) => (
                                                <div
                                                    ref={setNodeRef}
                                                    style={{
                                                        transform: CSS.Transform.toString(transform),
                                                        transition,
                                                    }}
                                                    className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 bg-white dark:bg-neutral-800 mb-4"
                                                >
                                                    <div className="flex items-center space-x-3 mb-4">
                                                        {/* 👇 ISOLATED MODULE DRAG HANDLE — type="button" added */}
                                                        <button
                                                            type="button"
                                                            {...attributes}
                                                            {...listeners}
                                                            className="p-2 text-neutral-400 hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300 cursor-grab active:cursor-grabbing"
                                                            aria-label="Drag to reorder module"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                                            </svg>
                                                        </button>
                                                        <span className="font-mono text-sm text-neutral-500 dark:text-neutral-400">
                                                            {moduleIndex + 1}.
                                                        </span>
                                                        <input
                                                            type="text"
                                                            value={module.title}
                                                            onChange={(e) => updateModule(moduleIndex, 'title', e.target.value)}
                                                            className="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                            placeholder="Module title"
                                                        />
                                                        <div className="flex space-x-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => addLesson(moduleIndex)}
                                                                className="px-3 py-1 bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
                                                            >
                                                                Add Lesson
                                                            </button>
                                                            {formData.modules.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (window.confirm('Are you sure you want to delete this entire module and all its lessons?')) {
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                modules: prev.modules.filter((_, i) => i !== moduleIndex)
                                                                            }));
                                                                        }
                                                                    }}
                                                                    className="px-3 py-1 bg-danger-500 hover:bg-danger-600 text-white text-xs font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-danger-500"
                                                                >
                                                                    Delete Module
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* --- START: LESSON LIST (NO DRAG & DROP) --- */}
                                                    <div className="ml-8 space-y-3">
                                                        {module.lessons.map((lesson, lessonIndex) => (
                                                            <div
                                                                key={lesson.id}
                                                                className="flex items-center space-x-3 p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg"
                                                            >
                                                                <span className="font-mono text-sm text-neutral-500 dark:text-neutral-400">
                                                                    {lessonIndex + 1}.
                                                                </span>
                                                                <input
                                                                    type="text"
                                                                    value={lesson.title}
                                                                    onChange={(e) => updateLesson(moduleIndex, lessonIndex, 'title', e.target.value)}
                                                                    className="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                                    placeholder="Lesson title"
                                                                />
                                                                <div className="flex space-x-2">
                                                                    {/* Lesson Actions */}
                                                                    <div className="flex flex-wrap gap-2 mt-2">

                                                                        {/* Add Rich text description */}

                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openTextEditorModal(moduleIndex, lessonIndex)}
                                                                            className="text-xs bg-yellow-500 text-white px-3 py-1 hover:bg-yellow-600 font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                                                        >
                                                                            {lesson.textDescription ? 'Edit Description' : 'Add Description'}
                                                                        </button>

                                                                        {/* Add Video */}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                if (!CheckInstructorSelected) {
                                                                                    toast.error('Please select an instructor first.');
                                                                                    return;
                                                                                }
                                                                                setCurrentLessonContext({ moduleIndex, lessonIndex, contentType: 'video' });
                                                                                setIsMediaModalOpen(true);
                                                                            }}
                                                                            className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                        >
                                                                            {lesson.video ? 'Change Video' : 'Add Video'}
                                                                        </button>

                                                                        {/* Add Resource */}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                if (!CheckInstructorSelected) {
                                                                                    toast.error('Please select an instructor first.');
                                                                                    return;
                                                                                }
                                                                                setCurrentLessonContext({ moduleIndex, lessonIndex, contentType: 'resource' });
                                                                                setIsMediaModalOpen(true);
                                                                            }}
                                                                            className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                                                        >

                                                                            {lesson.resources && lesson.resources.length > 0 ? 'Change Resource' : 'Add Resource'}
                                                                        </button>

                                                                        {/* Add/Edit Quiz Button */}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                if (!lesson || !lesson.id) {
                                                                                    console.error("Lesson data is missing");
                                                                                    toast.error("Lesson not found");
                                                                                    return;
                                                                                }

                                                                                if (!CheckInstructorSelected) {
                                                                                    toast.error('Please select an instructor first.');
                                                                                    return;
                                                                                }

                                                                                const lessonId = lesson.id

                                                                                const quizId = typeof lesson.quiz === "object" ? lesson.quiz?._id : lesson.quiz;

                                                                                const quizUrl = quizId
                                                                                    ? `/admin/cloud/edit-quiz/${lessonId}/${quizId}`
                                                                                    : `/admin/cloud/create-quiz/${lessonId}`;

                                                                                navigate(quizUrl);
                                                                            }}
                                                                            className={`px-3 py-1 text-white text-xs font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 ${lesson.quiz
                                                                                ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                                                                                : 'bg-amber-500 hover:bg-amber-600 focus:ring-amber-500'
                                                                                }`}
                                                                        >
                                                                            {lesson.quiz ? 'Edit Quiz' : 'Add Quiz'}
                                                                        </button>


                                                                        {/* Add/Edit Assignment */}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                if (!lesson || !lesson.id) {
                                                                                    console.error("Lesson data is missing");
                                                                                    toast.error("Lesson not found");
                                                                                    return;
                                                                                }

                                                                                if (!CheckInstructorSelected) {
                                                                                    toast.error('Please select an instructor first.');
                                                                                    return;
                                                                                }

                                                                                const lessonId = lesson.id

                                                                                const assignmentId = typeof lesson.assignment === "object" ? lesson.assignment?._id : lesson.assignment;

                                                                                const assignmentUrl = assignmentId
                                                                                    ? `/admin/cloud/edit-assignment/${lessonId}/${assignmentId}`
                                                                                    : `/admin/cloud/create-assignment/${lessonId}`;

                                                                                navigate(assignmentUrl);
                                                                            }}
                                                                            className={`px-3 py-1 text-white text-xs font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 ${lesson.assignment
                                                                                ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                                                                                : 'bg-amber-500 hover:bg-amber-600 focus:ring-amber-500'
                                                                                }`}
                                                                        >
                                                                            {lesson.assignment ? 'Edit Assignment' : 'Add Assignment'}
                                                                        </button>

                                                                        {/* Delete Lesson */}
                                                                        {module.lessons.length > 1 && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    if (window.confirm('Are you sure you want to delete this lesson?')) {
                                                                                        setFormData(prev => ({
                                                                                            ...prev,
                                                                                            modules: prev.modules.map((mod, i) =>
                                                                                                i === moduleIndex
                                                                                                    ? {
                                                                                                        ...mod,
                                                                                                        lessons: mod.lessons.filter((_, j) => j !== lessonIndex)
                                                                                                    }
                                                                                                    : mod
                                                                                            )
                                                                                        }));
                                                                                    }
                                                                                }}
                                                                                className="px-3 py-1 bg-danger-500 hover:bg-danger-600 text-white text-xs font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-danger-500"
                                                                            >
                                                                                Delete
                                                                            </button>
                                                                        )}

                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {/* --- END: LESSON LIST --- */}
                                                </div>
                                            )}
                                        </SortableItem>
                                    </React.Fragment>
                                ))}
                                {/* DROP INDICATOR AT END OF MODULE LIST */}
                                {activeModuleId && overModuleId === 'modules-end' && (
                                    <DropIndicator isVisible={true} />
                                )}
                            </SortableContext>
                        </DndContext>
                    </div>
                    {/* Submit Button */}
                    <div className="flex justify-end pt-6 border-t border-neutral-200 dark:border-neutral-700">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="py-3 px-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {isEditing ? 'Updating Course...' : 'Creating Course...'}
                                </span>
                            ) : (
                                isEditing ? 'Update Course' : 'Create Course'
                            )}
                        </button>
                    </div>
                </form>

                <MediaSelectorModal
                    isOpen={isMediaModalOpen}
                    onClose={() => setIsMediaModalOpen(false)}
                    onMediaSelect={handleMediaSelect}
                    selectedMedia={
                        currentLessonContext.contentType === 'introVideo'
                            ? formData?.introVideo
                                ? [{ _id: formData.introVideo }]
                                : []
                            : currentLessonContext.contentType === 'video'
                                ? formData.modules?.[currentLessonContext.moduleIndex]?.lessons?.[currentLessonContext.lessonIndex]?.video
                                    ? [
                                        {
                                            _id:
                                                formData.modules[currentLessonContext.moduleIndex].lessons[
                                                    currentLessonContext.lessonIndex
                                                ].video,
                                        },
                                    ]
                                    : []
                                : formData.modules?.[currentLessonContext.moduleIndex]?.lessons?.[
                                    currentLessonContext.lessonIndex
                                ]?.resources
                                    ? formData.modules[currentLessonContext.moduleIndex].lessons[
                                        currentLessonContext.lessonIndex
                                    ].resources.map((id) => ({ _id: id }))
                                    : []
                    }
                    contentType={currentLessonContext.contentType}
                />

                <CourseCreationGuideModal
                    isOpen={isGuideModalOpen}
                    onClose={() => setIsGuideModalOpen(false)}
                />


                {isTextModalOpen && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-900 w-[800px] max-h-[90vh] rounded-xl shadow-lg flex flex-col">

                            <div className="flex justify-between items-center p-4 border-b dark:border-b-gray-800">
                                <h2 className="text-lg font-semibold dark:text-white">Lesson Description</h2>
                                <button className='dark:text-white' onClick={() => setIsTextModalOpen(false)}>✕</button>
                            </div>

                            <div className="p-4 overflow-y-auto">

                                <Editor
                                    apiKey="z7v1her3jufqpkft4ixbai7zc3c88cb3706r531rzjmn2oc6"
                                    value={editorContent}
                                    onEditorChange={(content) => setEditorContent(content)}
                                    init={{
                                        height: 300,

                                        menubar: 'edit insert format',

                                        plugins: [
                                            "advlist", "anchor", "autolink", "charmap", "code", "fullscreen",
                                            "help", "image", "insertdatetime", "link", "lists", "media",
                                            "preview", "searchreplace", "table", "visualblocks",
                                        ],

                                        toolbar: "undo redo | styles | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image",

                                        menu: {
                                            insert: { title: 'Insert', items: 'image link media' },
                                            format: { title: 'Format', items: 'bold italic underline | alignleft aligncenter alignright' },
                                        },

                                        image_title: true,
                                        image_caption: true,

                                        automatic_uploads: false,
                                        images_upload_handler: null,

                                        file_picker_types: 'image',

                                        file_picker_callback: (callback) => {
                                            const input = document.createElement('input');
                                            input.type = 'file';
                                            input.accept = 'image/*';

                                            input.onchange = () => {
                                                const file = input.files[0];

                                                if (!file) return;

                                                if (file.size > 500 * 1024) {
                                                    alert('Image must be less than 500KB');
                                                    return;
                                                }

                                                const reader = new FileReader();

                                                reader.onload = () => {
                                                    callback(reader.result, { alt: file.name });
                                                };

                                                reader.readAsDataURL(file);
                                            };

                                            input.click();
                                        },

                                        content_style: 'body { font-family: Inter, sans-serif; font-size: 14px; }',
                                        skin: 'oxide-dark',
                                        content_css: 'dark'
                                    }}
                                />
                            </div>

                            <div className="flex justify-end gap-2 p-4 border-t dark:border-t-gray-800">
                                <button
                                    onClick={() => setIsTextModalOpen(false)}
                                    className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={saveTextDescription}
                                    className="py-3 px-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
            <FloatingChatButton />
        </DashboardLayout>
    );
};
export default AdminCourseCreatePage;